/**
 * Tour Guide Component
 * 
 * Main tour component using @reactour/tour to display interactive guided tours.
 * Handles tour display, navigation, and completion. Users can skip/cancel tours.
 */

import React, { useEffect, useState, useMemo } from 'react';
import { TourProvider as ReactTourProvider, useTour } from '@reactour/tour';
import { useTour as useTourContext } from '@/app/providers/TourContext.jsx';
import { useTranslation } from 'react-i18next';
import { getTourSteps } from '@/app/config/tours';
import logger from '@/shared/utils/logger.js';

const TourWrapper = ({ children }) => {
  const { t } = useTranslation();
  const {
    isTourOpen,
    currentTourId,
    stopTour,
    completeTour
  } = useTourContext();

  const { setIsOpen, currentStep, steps } = useTour();
  const [firstElementReady, setFirstElementReady] = useState(false);

  // Wait for first step element to be properly positioned before opening
  useEffect(() => {
    let timeoutId = null;
    let intervalId = null;
    
    if (isTourOpen && steps.length > 0) {
      const firstStep = steps[0];
      if (firstStep?.selector) {
        const checkFirstElement = () => {
          const element = document.querySelector(firstStep.selector);
          if (!element) {
            return false;
          }
          
          const rect = element.getBoundingClientRect();
          // Element must have dimensions
          if (rect.width === 0 && rect.height === 0) {
            return false;
          }
          
          // For settings page titles, ensure they're properly positioned
          if (firstStep.selector.includes('settings-page-title')) {
            // Check if element is actually visible and positioned correctly
            const computedStyle = window.getComputedStyle(element);
            const isVisible = computedStyle.display !== 'none' && 
                             computedStyle.visibility !== 'hidden' && 
                             computedStyle.opacity !== '0';
            
            if (!isVisible) {
              return false;
            }
            
            // Element should be in the viewport with reasonable positioning
            // Top should be > 0 (not at absolute top) or if at top, should have proper width
            if (rect.top === 0 && rect.left === 0) {
              // If at 0,0, check if it's actually the page title (should have width > 200px typically)
              if (rect.width < 200) {
                return false; // Probably not properly positioned yet
              }
            }
            
            // Element should have reasonable dimensions
            if (rect.width < 50 || rect.height < 10) {
              return false;
            }
          }
          
          // Element is ready
          setFirstElementReady(true);
          return true;
        };
        
        setFirstElementReady(false);
        
        // Initial delay to let DOM settle
        timeoutId = setTimeout(() => {
          if (!checkFirstElement()) {
            // Retry with interval
            let attempts = 0;
            const maxAttempts = 20;
            intervalId = setInterval(() => {
              attempts++;
              if (checkFirstElement() || attempts >= maxAttempts) {
                if (intervalId) {
                  clearInterval(intervalId);
                  intervalId = null;
                }
                if (attempts >= maxAttempts) {
                  setFirstElementReady(true); // Allow tour to proceed anyway
                }
              }
            }, 100);
          }
        }, 200);
      } else {
        setFirstElementReady(true);
      }
    } else {
      setFirstElementReady(false);
    }
    
    // Cleanup function
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };
  }, [isTourOpen, steps]);

  // Sync tour open state with context - only open if we have steps AND first element is ready
  useEffect(() => {
    logger.debug('TourWrapper sync', { isTourOpen, stepsLength: steps.length, currentTourId, firstElementReady });
    // Only open if tour is requested AND we have steps loaded AND first element is ready
    if (isTourOpen && steps.length > 0 && firstElementReady) {
      logger.debug('Opening tour with steps - first element is ready', { stepsLength: steps.length });
      setIsOpen(true);
    } else if (!isTourOpen) {
      setIsOpen(false);
    }
  }, [isTourOpen, steps.length, currentTourId, firstElementReady, setIsOpen]);

  return <>{children}</>;
};

const TourGuide = ({ children }) => {
  const { t } = useTranslation();
  const {
    isTourOpen,
    currentTourId,
    stopTour,
    completeTour
  } = useTourContext();

  // Get window width for responsive design
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const isMobile = windowWidth <= 768;
  const isTablet = windowWidth > 768 && windowWidth <= 1024;

  useEffect(() => {
    let resizeTimer = null;
    const handleResize = () => {
      // Debounce resize events to prevent excessive re-renders
      if (resizeTimer) {
        clearTimeout(resizeTimer);
      }
      resizeTimer = setTimeout(() => {
        setWindowWidth(window.innerWidth);
      }, 100);
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      if (resizeTimer) {
        clearTimeout(resizeTimer);
      }
    };
  }, []);

  // Get steps for the current tour - use useMemo to ensure they update when tourId changes
  // Re-compute when tour opens to catch dynamic DOM changes (like tab switching)
  const steps = useMemo(() => {
    if (!currentTourId) {return [];}
    const tourSteps = getTourSteps(currentTourId, t);
    logger.debug('Tour steps loaded', { 
      tourId: currentTourId, 
      stepsCount: tourSteps.length,
      steps: tourSteps.map(s => ({ selector: s.selector, hasContent: !!s.content }))
    });
    return tourSteps;
  }, [currentTourId, t, isTourOpen]);
  
  // Verify first step element exists before allowing tour to open
  useEffect(() => {
    if (isTourOpen && steps.length > 0) {
      const firstStep = steps[0];
      if (firstStep?.selector) {
        const checkFirstElement = () => {
          const element = document.querySelector(firstStep.selector);
          if (element) {
            const rect = element.getBoundingClientRect();
            // Element must have dimensions to be considered valid
            if (rect.width > 0 || rect.height > 0) {
              logger.debug('First tour step element is ready', { selector: firstStep.selector });
              return true;
            }
          }
          return false;
        };
        
        // Check immediately
        if (!checkFirstElement()) {
          // Wait for element with retries
          let attempts = 0;
          const maxAttempts = 30; // 3 seconds max wait
          const interval = setInterval(() => {
            attempts++;
            if (checkFirstElement() || attempts >= maxAttempts) {
              clearInterval(interval);
              if (attempts >= maxAttempts) {
                logger.error('First tour step element not found after waiting', { selector: firstStep.selector });
              }
            }
          }, 100);
          
          return () => clearInterval(interval);
        }
      }
    }
  }, [isTourOpen, steps]);
  
  // Debug logging - more detailed
  useEffect(() => {
    logger.debug('TourGuide state', { 
      isTourOpen, 
      currentTourId, 
      stepsCount: steps.length,
      hasSteps: steps.length > 0,
      shouldOpen: isTourOpen && steps.length > 0
    });
    
    if (isTourOpen && currentTourId && steps.length === 0) {
      logger.error('Tour is open but no steps found', { currentTourId });
    }
  }, [isTourOpen, currentTourId, steps.length]);

  // Custom styles for the tour - responsive
  const tourStyles = useMemo(() => ({
    popover: (base) => ({
      ...base,
      '--reactour-accent': 'var(--accent, #004895)',
      borderRadius: isMobile ? '10px' : '12px',
      backgroundColor: 'var(--card, #ffffff)',
      color: 'var(--text-primary, #000000)',
      boxShadow: 'var(--shadow-xl, 0 20px 25px -5px rgba(0, 0, 0, 0.1))',
      padding: isMobile ? '16px' : isTablet ? '18px' : '20px',
      maxWidth: isMobile ? 'calc(100vw - 32px)' : isTablet ? '360px' : '400px',
      border: '1px solid var(--border-secondary, #e5e7eb)',
      fontSize: isMobile ? '14px' : '15px',
      lineHeight: '1.5'
    }),
    maskArea: (base) => ({
      ...base,
      rx: isMobile ? 6 : 8
    }),
    badge: (base) => ({
      ...base,
      left: isMobile ? '14px' : '18px',
      right: 'auto',
      top: isMobile ? '4px' : '6px',
      backgroundColor: 'var(--accent, #004895)',
      color: 'var(--text-inverse, #ffffff)',
      fontSize: isMobile ? '10px' : '11px',
      fontWeight: '600',
      padding: isMobile ? '2px 7px' : '3px 9px',
      borderRadius: '10px',
      minWidth: isMobile ? '20px' : '24px',
      height: isMobile ? '18px' : '20px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxShadow: '0 2px 4px rgba(0, 72, 149, 0.2)',
      border: '2px solid var(--card, #ffffff)',
      zIndex: 1001,
      lineHeight: 1
    }),
    close: (base) => ({
      ...base,
      right: isMobile ? '10px' : '14px',
      top: isMobile ? '10px' : '14px',
      width: isMobile ? '18px' : '20px',
      height: isMobile ? '18px' : '20px',
      fontSize: isMobile ? '13px' : '15px',
      color: 'var(--text-muted, #6b7280)',
      backgroundColor: 'transparent',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      border: 'none',
      transition: 'all 0.2s ease',
      zIndex: 1000,
      opacity: 0.6,
      '&:hover': {
        color: 'var(--text-primary, #000000)',
        backgroundColor: 'var(--bg-hover, #f3f4f6)',
        opacity: 1,
        transform: 'scale(1.08)'
      },
      '&:active': {
        transform: 'scale(0.92)'
      }
    })
  }), [isMobile, isTablet]);

  const handleClose = () => {
    // Mark tour as complete when user explicitly closes it
    if (currentTourId) {
      completeTour(currentTourId);
    }
    stopTour();
  };

  // Verify that step selectors exist in the DOM before opening
  useEffect(() => {
    if (isTourOpen && steps.length > 0) {
      logger.debug('Checking if tour elements exist in DOM');
      steps.forEach((step, index) => {
        const element = document.querySelector(step.selector);
        if (!element) {
          logger.warn(`Step ${index + 1} selector not found`, { selector: step.selector, stepIndex: index + 1 });
        } else {
          logger.debug(`Step ${index + 1} element found`, { selector: step.selector, stepIndex: index + 1 });
        }
      });
    }
  }, [isTourOpen, steps]);

  // Wait for elements to be available before opening tour
  const [elementsReady, setElementsReady] = useState(false);
  
  useEffect(() => {
    if (isTourOpen && steps.length > 0) {
      // Check if step elements exist AND have valid dimensions
      const checkElements = () => {
        // Check first step element especially carefully - it must be visible and positioned correctly
        const firstStep = steps[0];
        if (firstStep?.selector) {
          const firstElement = document.querySelector(firstStep.selector);
          if (!firstElement) {
            logger.debug('First step element not found yet', { selector: firstStep.selector });
            return false;
          }
          
          const firstRect = firstElement.getBoundingClientRect();
          // First element must have dimensions (not at 0,0 with no size)
          if (firstRect.width === 0 && firstRect.height === 0) {
            logger.debug('First step element has no dimensions yet', { selector: firstStep.selector });
            return false;
          }
          
          // Check if element is actually in the viewport (not at 0,0)
          // If top and left are both 0, it might be positioned incorrectly
          if (firstRect.top === 0 && firstRect.left === 0 && firstRect.width > 0 && firstRect.height > 0) {
            // This might be okay if it's actually at the top-left, but let's verify it's visible
            const computedStyle = window.getComputedStyle(firstElement);
            if (computedStyle.display === 'none' || computedStyle.visibility === 'hidden' || computedStyle.opacity === '0') {
              logger.debug('First step element is hidden', { selector: firstStep.selector });
              return false;
            }
          }
          
          // Additional check: ensure element is in the document flow
          const isInViewport = firstRect.top >= 0 && firstRect.left >= 0 && 
                               firstRect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
                               firstRect.right <= (window.innerWidth || document.documentElement.clientWidth);
          
          // For settings page title, it should be near the top but not at absolute 0,0
          // Allow some tolerance for elements that are legitimately at the top
          if (firstStep.selector.includes('settings-page-title')) {
            // Page title should be visible and have reasonable positioning
            if (firstRect.top < 0 || firstRect.left < 0) {
              logger.debug('Page title element not properly positioned yet', { selector: firstStep.selector });
              return false;
            }
          }
          
          logger.debug('First step element is ready', { selector: firstStep.selector, rect: firstRect });
        }
        
        // Check all other elements exist
        const allElementsExist = steps.every(step => {
          const element = document.querySelector(step.selector);
          return element !== null;
        });
        
        if (allElementsExist) {
          setElementsReady(true);
          return true;
        }
        return false;
      };
      
      // Reset ready state when tour opens
      setElementsReady(false);
      
      // Initial check with a small delay to allow DOM to settle
      setTimeout(() => {
        if (checkElements()) {
          return;
        }
        
        // If elements don't exist, wait and retry
        let attempts = 0;
        const maxAttempts = 50; // 5 seconds max wait (50 * 100ms) - longer for settings pages
        
        const interval = setInterval(() => {
          attempts++;
          if (checkElements() || attempts >= maxAttempts) {
            clearInterval(interval);
            if (attempts >= maxAttempts) {
              logger.warn('Some tour elements not found after waiting');
              // Still set ready to allow tour to proceed (it will handle missing elements)
              setElementsReady(true);
            }
          }
        }, 100);
        
        return () => clearInterval(interval);
      }, 150); // Initial delay to let DOM settle
      
    } else {
      setElementsReady(false);
    }
  }, [isTourOpen, steps]);
  
  // Calculate if tour should be open - ensure we wait for steps to load AND elements to be ready
  const shouldOpen = isTourOpen && currentTourId && steps.length > 0 && elementsReady;
  
  logger.debug('TourGuide render', { 
    isTourOpen, 
    currentTourId, 
    stepsLength: steps.length,
    shouldOpen,
    stepsPreview: steps.slice(0, 2).map(s => ({ selector: s.selector }))
  });

  return (
    <ReactTourProvider
      key={currentTourId} // Force re-render when tour ID changes
      steps={steps}
      isOpen={shouldOpen}
      onRequestClose={handleClose}
      onClickMask={handleClose}
      showBadge={true}
      showNavigation={true}
      showCloseButton={true}
      beforeStepChange={(target) => {
        // Verify the target element exists before moving to the step
        if (target) {
          const rect = target.getBoundingClientRect();
          // If element is at 0,0 or has no dimensions, it might not be visible
          if (rect.width === 0 && rect.height === 0) {
            logger.warn('Tour step target has no dimensions', { target });
            // Try to find the element again
            const selector = steps.find(s => {
              const el = document.querySelector(s.selector);
              return el === target;
            })?.selector;
            if (selector) {
              const newElement = document.querySelector(selector);
              if (newElement && (newElement.getBoundingClientRect().width > 0 || newElement.getBoundingClientRect().height > 0)) {
                return newElement;
              }
            }
          }
        }
        return target;
      }}
      afterOpen={(target, stepIndex) => {
        logger.debug('Tour step opened', { target, stepIndex, selector: steps[stepIndex]?.selector });
        if (target) {
          // Wait longer for first step on settings pages to ensure content is fully rendered
          const delay = stepIndex === 0 ? 400 : 150;
          setTimeout(() => {
            const rect = target.getBoundingClientRect();
            // Check if element is actually visible and has dimensions
            // Also check it's not at 0,0 (which indicates it's not properly positioned)
            if ((rect.width > 0 || rect.height > 0) && !(rect.top === 0 && rect.left === 0 && rect.width === 0 && rect.height === 0)) {
            const scrollOptions = isMobile 
              ? { behavior: 'smooth', block: 'start' }
              : { behavior: 'smooth', block: 'center' };
            target.scrollIntoView(scrollOptions);
            } else {
              logger.warn('Tour step target not visible or at 0,0, trying to find element by selector');
              // Try to find the element by selector from steps array
              const currentStep = steps[stepIndex];
              if (currentStep?.selector) {
                // Wait a bit more and try again
                setTimeout(() => {
                  const element = document.querySelector(currentStep.selector);
                  if (element) {
                    const elementRect = element.getBoundingClientRect();
                    if (elementRect.width > 0 || elementRect.height > 0) {
                      const scrollOptions = isMobile 
                        ? { behavior: 'smooth', block: 'start' }
                        : { behavior: 'smooth', block: 'center' };
                      element.scrollIntoView(scrollOptions);
                      logger.debug('Found element by selector and scrolled to it');
                    } else {
                      logger.error('Element found but has no dimensions', { selector: currentStep.selector });
                    }
                  } else {
                    logger.error('Element not found by selector', { selector: currentStep.selector });
                  }
                }, 200);
              }
            }
          }, delay);
        } else {
          logger.error('Tour step opened but target is null for step', { stepIndex });
          // Try to find the element by selector if target is null
          const currentStep = steps[stepIndex];
          if (currentStep?.selector) {
            setTimeout(() => {
              const element = document.querySelector(currentStep.selector);
              if (element) {
                const scrollOptions = isMobile 
                  ? { behavior: 'smooth', block: 'start' }
                  : { behavior: 'smooth', block: 'center' };
                element.scrollIntoView(scrollOptions);
                logger.debug('Found element by selector when target was null');
              }
            }, 300);
          }
        }
      }}
      styles={tourStyles}
      padding={{ popover: isMobile ? [16, 16] : isTablet ? [18, 18] : [20, 20] }}
      className="reactour__custom"
      disableBodyScroll={false}
      disableInteraction={false}
      rtl={false}
      classNamePrefix="reactour"
    >
      <TourWrapper>
        {children}
      </TourWrapper>
    </ReactTourProvider>
  );
};

export default TourGuide;

